import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import sessionStorageStore from 'store/storages/sessionStorage';

import useDismissiblePersistent from '../../../../../../hooks/useDismissiblePersistent';
import { STORE_NAMESPACE } from '../../../../../../components/global/DismissiblePersistent';
import { isAlbumOwner } from '../../../../../../util/account';
import Button from '../../../../../../components/global/Button';
import canUseDOM from '../../../../../../util/can-use-dom';
import VideoGuestBookContributorVideo from './components/VideoGuestBookContributorVideo';
import VideoGuestBookContributorRecordModal from './components/VideoGuestBookContributorRecordModal/VideoGuestBookContributorRecordModal';
import { videoIsProcessed } from '../../../../../../util/video';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  greetingVideo: PropTypes.shape({
    id: PropTypes.string,
  }),
  onRecordGreeting: PropTypes.func,
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
};

const VideoGuestBookContributorBanner = ({
  user,
  album,
  greetingVideo,
  onRecordGreeting,
  albumFolder,
}) => {
  // There are 5 scenarios here:
  // 1. There is a Greeting video you have not seen & you have not recorded a video. (greetingVideoVisible)
  // 2. There is no greeting video, and you have not recorded a video. (videoRecordFooterBannerVisible)
  // 3. There is a Greeting video you have not seen & you have recorded a Video (greetingVideoVisible but won't trigger the videoRecordFooterBannerVisible)
  // 4. There is a Greeting video you have seen & you have not recorded a video (videoRecordBannerVisible)
  // 5. There is no Greeting video (or you have seen it) & you have recorded. (We do nothing).
  const {
    visible: greetingVideoVisible,
    onDismiss: onDismissGreetingVideo,
  } = useDismissiblePersistent(
    `${STORE_NAMESPACE}_VGB_GREETING_VIDEO_${album?.id}`,
  );
  const {
    visible: videoRecordFooterBannerVisible,
    onDismiss: onDismissVideoRecordFooterBanner,
  } = useDismissiblePersistent(
    `${STORE_NAMESPACE}_VGB_GREETING_FOOTER_BANNER_${album?.id}`,
    sessionStorageStore,
  );
  const {
    visible: videoRecordBannerVisible,
    onDismiss: onDismissVideoRecordBanner,
  } = useDismissiblePersistent(
    `${STORE_NAMESPACE}_VGB_SOLICIT_BANNER_${album?.id}`,
    sessionStorageStore,
  );

  const isAlbumOwnerBool = isAlbumOwner(user, album);
  const hasUploadedVideo = (album?.user_uploaded_videos?.total ?? 0) > 0;

  const [displayVideoEndBanner, setDisplayVideoEndBanner] = useState(false);

  const hasSeenGreetingVideo = greetingVideo && !greetingVideoVisible;

  useEffect(() => {
    // The goal here is when there is no greeting video, we attempt to solicit
    // This is case #2 to be specific.
    if (
      album &&
      videoRecordBannerVisible &&
      !hasSeenGreetingVideo &&
      !hasUploadedVideo
    ) {
      onDismissVideoRecordBanner();
      setDisplayVideoEndBanner(true);
    }
  }, [album, canUseDOM, videoRecordBannerVisible]);

  // Case #5 comes in play :D
  if (
    !album ||
    isAlbumOwnerBool ||
    (hasUploadedVideo && hasSeenGreetingVideo)
  ) {
    return null;
  }

  const footerBannerClasses = classNames({
    'video-guest-book-contributor-banner': true,
    'video-guest-book-contributor-banner-footer': true,
    'video-guest-book-contributor-banner-open':
      videoRecordFooterBannerVisible &&
      !displayVideoEndBanner &&
      !greetingVideoVisible,
  });

  if (!canUseDOM) {
    return null;
  }

  return (
    <>
      {greetingVideoVisible &&
        greetingVideo &&
        videoIsProcessed(greetingVideo) && (
          <VideoGuestBookContributorVideo
            greetingVideo={greetingVideo}
            onDismiss={() => {
              onDismissGreetingVideo();

              if (!hasUploadedVideo) {
                setDisplayVideoEndBanner(true);
              }
            }}
          />
        )}

      <VideoGuestBookContributorRecordModal
        forceOpen={displayVideoEndBanner}
        albumFolder={albumFolder}
        onRecord={() => {
          onRecordGreeting();
          onDismissVideoRecordFooterBanner();
          setDisplayVideoEndBanner(false);
        }}
        onDismiss={() => {
          onDismissVideoRecordFooterBanner();
          setDisplayVideoEndBanner(false);
        }}
      />

      <div className={footerBannerClasses}>
        <div className="d-flex align-items-center justify-content-center">
          <p className="mb-0">
            Record a video to express your well wishes to the happy couple!
          </p>
          <Button
            className="ml-2"
            btnSize="sm"
            pill
            onClick={() => {
              onRecordGreeting();
              onDismissVideoRecordFooterBanner();
            }}
          >
            Record Now
          </Button>
        </div>
        <Button
          btnStyle="link"
          className="video-guest-book-contributor-banner-footer-dismiss"
          onClick={onDismissVideoRecordFooterBanner}
        >
          Maybe later
        </Button>
      </div>
    </>
  );
};

VideoGuestBookContributorBanner.propTypes = propTypes;

export default VideoGuestBookContributorBanner;
